import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray, ControlContainer, FormControlName } from '@angular/forms';
import { RestCallerService } from '../common/services/rest-caller.service';
import { CalculationEngineConstants } from './calculation-engine-constants';
import { environment } from '../../environments/environment';

declare var $: any;
declare var swal: any;
declare var alasql: any;

@Component({
  selector: 'app-calculation-engine',
  templateUrl: './calculation-engine.component.html',
  styleUrls: ['./calculation-engine.component.css']
})

export class CalculationEngineComponent implements OnInit {

  @Input() employeeState: String;
  @Input() employee: Object;
  @Input() client: Object;

  calculationConstants: any;
  checkDateInput: Date;
  formType: string = '';
  selectedState: any;
  selectedStateAbbv: string = '';
  selectedPayFreq: any;
  selectedGrossPayType: any;
  selectedFederalFilingStatus: any;
  selectedStateFilingStatus: any;
  selectedStateFilingStatuses: any[];
  payCheckCityForm: FormGroup;
  fieldMap: Map<String, String> = new Map<String, String>();

  emailHtmlInputString: string = "";
  emailHtmlOutputString: string = "";

  clientApiCount: number;
  apiTextColor: string = "black";
  environment: any;

  showLoader: boolean = false;

  constructor(private restCallingService: RestCallerService) {
    this.calculationConstants = CalculationEngineConstants;
    this.environment = environment;
  }

  ngOnInit() {
    this.payCheckCityForm = new FormGroup({
      'checkDate': new FormControl(null, [Validators.required]),
      'state': new FormControl(null, [Validators.required]),
      'grossPay': new FormControl(null),
      'grossPayType': new FormControl(null),
      'netPay': new FormControl(null),
      'payFrequency': new FormControl(null),
      'federalFilingStatusType': new FormControl(null),
      'federalAllowances': new FormControl(null),
      'totalStateAllowances': new FormControl(null),
      'regularAllowances': new FormControl(null),
      'basicAllowances': new FormControl(null),
      'SDI': new FormControl(null),
      'SUI': new FormControl(null),
      'totalCityAllowances1': new FormControl(null),
      'filingStatus': new FormControl(null),
      'familyLeave': new FormControl(null),
      'address': new FormControl(null),
      'city': new FormControl(null),
      'zip': new FormControl(null),
      'zip4': new FormControl(null),
      'resident': new FormControl(null),
      'personalExemptions': new FormControl(null),
      'withholdingCode': new FormControl(null),
    });
    this.setDecimalFormatting();
    this.buildFieldMap();
  }

  /* Called whenever the Form Type is selected (or changed). This function will set all the default form values for the selected form
     Defaults:
     Pay Frequency: Weekly
     GrossPayType: Per Pay Period
     Federal Filing Status: Single
  */
  initializeDefaultFormValues() {
    let payFrequencyIndex = this.calculationConstants.PayFrequencyTypes.findIndex(freq => freq.code == this.employee['payFrequencyType']);
    payFrequencyIndex = payFrequencyIndex > -1 ? payFrequencyIndex : 1;
    this.selectedPayFreq = this.calculationConstants.PayFrequencyTypes[payFrequencyIndex].code;
    let stateIndex = this.calculationConstants.SupportedStates.findIndex(state => state.abbv == this.employeeState);
    stateIndex = stateIndex > -1 ? stateIndex : null;
    this.selectedState = this.calculationConstants.SupportedStates[stateIndex];
    let grossPayTypeIndex = this.calculationConstants.GrossPayTypes.findIndex(type => type.code == 'PAY_PER_PERIOD');
    grossPayTypeIndex = grossPayTypeIndex > -1 ? grossPayTypeIndex : 0; 
    this.selectedGrossPayType = this.calculationConstants.GrossPayTypes[grossPayTypeIndex].code;
    let federalFilingStatus = this.fieldMap.get(this.employee['maritalStatus']);
    let federalFilingStatusIndex = this.calculationConstants.FederalFilingStatuses.findIndex(status => status.code == federalFilingStatus);
    federalFilingStatusIndex = federalFilingStatusIndex > -1 ? federalFilingStatusIndex : 0; 
    this.selectedFederalFilingStatus = this.calculationConstants.FederalFilingStatuses[federalFilingStatusIndex].code;
    this.setStateFilingStatus(this.selectedState);
    this.setIsResident(this.selectedState);
    if (this.employee['federalExemptions'])
      this.payCheckCityForm.controls['federalAllowances'].setValue(this.employee['federalExemptions']);
    else 
      this.payCheckCityForm.controls['federalAllowances'].setValue(0);
    if (this.employee['stateExemptions'])
      this.payCheckCityForm.controls['totalStateAllowances'].setValue(this.employee['stateExemptions']);
    else 
      this.payCheckCityForm.controls['totalStateAllowances'].setValue(0);
    this.payCheckCityForm.controls['totalCityAllowances1'].setValue(0);
    this.payCheckCityForm.controls['basicAllowances'].setValue(0);
    this.payCheckCityForm.controls['regularAllowances'].setValue(0);
    this.payCheckCityForm.controls['personalExemptions'].setValue(0);
    this.payCheckCityForm.controls['familyLeave'].setValue('Yes');
    this.payCheckCityForm.controls['SDI'].setValue('Yes');
    this.payCheckCityForm.controls['SUI'].setValue('Yes');
    this.payCheckCityForm.controls['address'].setValue(this.client['address'] ? this.client['address'] : '');
    this.payCheckCityForm.controls['city'].setValue(this.client['city'] ? this.client['city'] : '');
    this.payCheckCityForm.controls['zip'].setValue(this.client['zip'] ? this.client['zip'] : '');
  }

  /* When the selected state is changed, change the state specific filing stauses and check if the employee is a resident */
  onStateChange(event) {
    this.setStateFilingStatus(event);
    this.setIsResident(event);
  }

  setIsResident(currentSelectedState: any) {
    if (currentSelectedState) {
      if (this.employee['state'] == currentSelectedState.abbv)
        this.payCheckCityForm.controls['resident'].setValue('Yes');
      else
        this.payCheckCityForm.controls['resident'].setValue('No')
    }
  }

  /* Provides the correct default state filing status and list of available state filing satuses based on the selected state
     Default: Single (Index 0) 
  */ 
  setStateFilingStatus(currentSelectedState: any) {
    if (currentSelectedState) {
      let employeeMaritalStatus = this.employee['maritalStatus'] ? this.employee['maritalStatus'] : null;
      if (currentSelectedState.abbv == 'GA') {
        let stateFilingStatusIndex = this.calculationConstants.GAFilingStatuses.findIndex(status => status.name.toUpperCase().includes(employeeMaritalStatus));
        stateFilingStatusIndex = stateFilingStatusIndex > -1 ? stateFilingStatusIndex : 0;
        this.selectedStateFilingStatus = this.calculationConstants.GAFilingStatuses[stateFilingStatusIndex].code;
        this.selectedStateFilingStatuses = this.calculationConstants.GAFilingStatuses;
      }
      else if (currentSelectedState.abbv == 'NJ') {
        let stateFilingStatusIndex = this.calculationConstants.NJFilingStatuses.findIndex(status => status.name.toUpperCase().includes(employeeMaritalStatus));
        stateFilingStatusIndex = stateFilingStatusIndex > -1 ? stateFilingStatusIndex : 0;
        this.selectedStateFilingStatus = this.calculationConstants.NJFilingStatuses[stateFilingStatusIndex].code;
        this.selectedStateFilingStatuses = this.calculationConstants.NJFilingStatuses;
      }
      else if (currentSelectedState.abbv == 'NY') {
        let stateFilingStatusIndex = this.calculationConstants.NYFilingStatuses.findIndex(status => status.name.toUpperCase().includes(employeeMaritalStatus));
        stateFilingStatusIndex = stateFilingStatusIndex > -1 ? stateFilingStatusIndex : 0;
        this.selectedStateFilingStatus = this.calculationConstants.NYFilingStatuses[stateFilingStatusIndex].code;
        this.selectedStateFilingStatuses = this.calculationConstants.NYFilingStatuses;
      }
      else if (currentSelectedState.abbv == 'DC') {
        let stateFilingStatusIndex = this.calculationConstants.DCFilingStatuses.findIndex(status => status.name.toUpperCase().includes(employeeMaritalStatus));
        stateFilingStatusIndex = stateFilingStatusIndex > -1 ? stateFilingStatusIndex : 0;
        this.selectedStateFilingStatus = this.calculationConstants.DCFilingStatuses[stateFilingStatusIndex].code;
        this.selectedStateFilingStatuses = this.calculationConstants.DCFilingStatuses;
      }
      else if (currentSelectedState.abbv == 'UT' || currentSelectedState.abbv == 'MD') {
        let stateFilingStatusIndex = this.calculationConstants.UTMDFilingStatuses.findIndex(status => status.name.toUpperCase().includes(employeeMaritalStatus));
        stateFilingStatusIndex = stateFilingStatusIndex > -1 ? stateFilingStatusIndex : 0;
        this.selectedStateFilingStatus = this.calculationConstants.UTMDFilingStatuses[stateFilingStatusIndex].code;
        this.selectedStateFilingStatuses = this.calculationConstants.UTMDFilingStatuses;
      }
      else {
        let stateFilingStatusIndex = this.calculationConstants.StateFilingStatuses.findIndex(status => status.name.toUpperCase() == employeeMaritalStatus);
        stateFilingStatusIndex = stateFilingStatusIndex > -1 ? stateFilingStatusIndex : 0;
        this.selectedStateFilingStatus = this.calculationConstants.StateFilingStatuses[stateFilingStatusIndex].code;
        this.selectedStateFilingStatuses = this.calculationConstants.StateFilingStatuses;
      }
    }
  }

  /* Code to format currency fields with the appropriate decimal places */
  setDecimalFormatting() {
    $('.two-place-decimal').on('change', (function () {
      $(this).val(parseFloat($(this).val()).toFixed(2).replace('NaN', ''));
    }));
  }

  buildFieldMap() {
    this.fieldMap.set('federal', 'Federal');
    this.fieldMap.set('medicare', 'Medicare');
    this.fieldMap.set('fica', 'Social Security'); //temporary until we hear a response from paycheckCity 8/7/2018
    this.fieldMap.set('eic', 'EIC');
    this.fieldMap.set('state', 'State');
    this.fieldMap.set('netPay', 'Net Pay');
    this.fieldMap.set('grossPay', 'Gross Pay');
    this.fieldMap.set('grossPayType', 'Gross Pay Type');
    this.fieldMap.set('checkDate', 'Check Date');
    this.fieldMap.set('payFrequency', 'Pay Frequency');
    this.fieldMap.set('federalFilingStatusType', 'Federal Filing Status Type');
    this.fieldMap.set('federalAllowances', 'Federal Allowances');
    this.fieldMap.set('TOTALALLOWANCES', 'Total State Allowances');
    this.fieldMap.set('TOTALALLOWANCESNYC', 'Total Allowances NYC');
    this.fieldMap.set('REGULARALLOWANCES', 'Regular Allowances');
    this.fieldMap.set('FILINGSTATUS', 'Filing Status');
    this.fieldMap.set('familyLeave', 'Family Leave');
    this.fieldMap.set('address1', 'Address');
    this.fieldMap.set('city', 'City');
    this.fieldMap.set('zip', 'Zip');
    this.fieldMap.set('zip4', 'Zip4');
    this.fieldMap.set('resident', 'Resident');
    this.fieldMap.set('PERSONALEXEMPTIONS', 'Personal Exemptions');
    this.fieldMap.set('WITHHOLDINGCODE', 'Withholding Code');
    this.fieldMap.set('SINGLE', 'SINGLE');
    this.fieldMap.set('MARRIED', 'MARRIED');
    this.fieldMap.set('MARRIED_WITHHOLDING', 'MARRIED_USE_SINGLE_RATE');
    this.fieldMap.set('OTHER', 'NONRESIDENT_ALIEN');
    this.fieldMap.set('GROSSUP', 'Gross Up');
    this.fieldMap.set('SALARY', 'Salary');
  }

  showModal() {
    this.payCheckCityForm.reset();
    this.formType = '';
    this.checkDateInput = new Date();
    this.getCalculationApiCountForClient();
    $('#CalculationEngineModal').modal('show');
  }

  closeModal() {
    this.payCheckCityForm.reset();
    this.formType = '';
    $('#CalculationEngineModal').modal('hide');
  }

  /* Constructs the JSON and output HTML for the fixed/general fields which apply to every state */
  setFixedFields() {
    let generalFields = {};
    let divTag = '<div style="font-weight: 600; width:25%; float:left; padding-left:10px;">'
    this.emailHtmlInputString += '<div style="background:#115E67; padding:5px; color:#FFFFFF; width: 98%; padding-left: 10px; margin-top:20px"> Calculation Input </div> <br/>';
    this.emailHtmlInputString += divTag + 'Calculation Type' + ':</div> ' + this.fieldMap.get(this.formType) + '<br/>';
    if (this.payCheckCityForm.controls['state'].value) {
      generalFields['state'] = this.payCheckCityForm.controls['state'].value.abbv;
      this.emailHtmlInputString += divTag + "State" + ':</div> ' + generalFields['state'] + '<br/>';
    }
    if (this.formType == 'SALARY') {
      if (this.payCheckCityForm.controls['grossPay'].value) {
        generalFields['grossPay'] = Number(this.payCheckCityForm.controls['grossPay'].value).toFixed(2);
        this.emailHtmlInputString += divTag + "Gross Pay" + ':</div> ' + this.formatCurrency(generalFields['grossPay']) + '<br/>';
      }
    }
    else if (this.formType == 'GROSSUP') {
      if (this.payCheckCityForm.controls['netPay'].value) {
        generalFields['netPay'] = Number(this.payCheckCityForm.controls['netPay'].value).toFixed(2);
        this.emailHtmlInputString += divTag + "Net Pay" + ':</div> ' + this.formatCurrency(generalFields['netPay']) + '<br/>';
      }
    }
    if (this.formType == 'SALARY' && this.payCheckCityForm.controls['grossPayType'].value) {
      generalFields['grossPayType'] = this.payCheckCityForm.controls['grossPayType'].value;
      let grossPayType = this.calculationConstants.GrossPayTypes.find(obj => obj.code == this.payCheckCityForm.controls['grossPayType'].value).name;
      this.emailHtmlInputString += divTag + "Gross Pay Type" + ':</div> ' + grossPayType + '<br/>';
    }
    if (this.checkDateInput)
      generalFields['checkDate'] = this.checkDateInput.getTime();
    if (this.payCheckCityForm.controls['payFrequency'].value) {
      generalFields['payFrequency'] = this.payCheckCityForm.controls['payFrequency'].value;
      let payFreq = this.calculationConstants.PayFrequencyTypes.find(obj => obj.code == generalFields['payFrequency']).name;
      this.emailHtmlInputString += divTag + "Pay Frequency" + ':</div> ' + payFreq + '<br/>';
    }
    if (this.payCheckCityForm.controls['federalFilingStatusType'].value) {
      generalFields['federalFilingStatusType'] = this.payCheckCityForm.controls['federalFilingStatusType'].value;
      let filingStatus = this.calculationConstants.FederalFilingStatuses.find(obj => obj.code == generalFields['federalFilingStatusType']).name;
      this.emailHtmlInputString += divTag + "Filing Status" + ':</div> ' + filingStatus + '<br/>';
    }
    if (this.payCheckCityForm.controls['federalAllowances'].value != null) {
      generalFields['federalAllowances'] = parseInt(this.payCheckCityForm.controls['federalAllowances'].value);
      this.emailHtmlInputString += divTag + "Federal Allowances" + ':</div> ' + generalFields['federalAllowances'] + '<br/>';
    }
    return generalFields;
  }

  /* Constructs the JSON and output HTML for the local fields, which are fields for the particular state that has been selected */
  setLocalFields() {
    let stateInfo = {};
    let parms = [];
    let local = {};
    let stateCode = '';
    let divTag = '<div style="font-weight: 600; width:25%; float:left;padding-left:10px;">'
    let stateEntry = this.calculationConstants.stateCodes.find(entry => entry.abbv == this.selectedState.abbv);
    if (stateEntry) {
      stateCode = stateEntry.code;
    }
    if (this.calculationConstants.sdiStates.includes(this.selectedState.abbv)) {
      if (this.payCheckCityForm.controls['SDI'].value) {
        parms.push({ name: stateCode + '-000-0000-SDI-000', value: this.getBooleanFromYesNo(this.payCheckCityForm.controls['SDI'].value) });
        this.emailHtmlInputString += divTag + "SDI" + ':</div> ' + this.payCheckCityForm.controls['SDI'].value + '<br/>';
      }
    }
    if (this.calculationConstants.suiStates.includes(this.selectedState.abbv)) {
      if (this.payCheckCityForm.controls['SUI'].value) {
        parms.push({ name: stateCode + '-000-0000-SUI-000', value: this.getBooleanFromYesNo(this.payCheckCityForm.controls['SUI'].value) });
        this.emailHtmlInputString += divTag + "SUI" + ':</div> ' + this.payCheckCityForm.controls['SUI'].value + '<br/>';
      }
    }
    if (this.calculationConstants.totalAllowanceStates.includes(this.selectedState.abbv)) {
      if (this.payCheckCityForm.controls['totalStateAllowances'].value != null) {
        parms.push({ name: 'TOTALALLOWANCES', value: this.payCheckCityForm.controls['totalStateAllowances'].value });
        this.emailHtmlInputString += divTag + "Total Allowances" + ':</div> ' + this.payCheckCityForm.controls['totalStateAllowances'].value + '<br/>';
      }
    }
    if (this.selectedState.abbv == 'NY') {
      if (this.payCheckCityForm.controls['totalCityAllowances1'].value != null) {
        parms.push({ name: 'TOTALALLOWANCESNYC', value: this.payCheckCityForm.controls['totalCityAllowances1'].value });
        this.emailHtmlInputString += divTag + "Total Allowances NYC" + ':</div> ' + this.payCheckCityForm.controls['totalCityAllowances1'].value + '<br/>';
      }
    }
    if (this.calculationConstants.regularAllowanceStates.includes(this.selectedState.abbv)) {
      if (this.payCheckCityForm.controls['regularAllowances'].value != null) {
        parms.push({ name: 'REGULARALLOWANCES', value: this.payCheckCityForm.controls['regularAllowances'].value });
        this.emailHtmlInputString += divTag + "Regular Allowances" + ':</div> ' + this.payCheckCityForm.controls['regularAllowances'].value + '<br/>';
      }
    }
    if (this.calculationConstants.basicAllowanceStates.includes(this.selectedState.abbv)) {
      if (this.payCheckCityForm.controls['basicAllowances'].value != null) {
        parms.push({ name: 'BASICALLOWANCES', value: this.payCheckCityForm.controls['basicAllowances'].value });
        this.emailHtmlInputString += divTag + "Basic Allowances" + ':</div> ' + this.payCheckCityForm.controls['basicAllowances'].value + '<br/>';
      }
    }
    if (this.calculationConstants.filingStatusStates.includes(this.selectedState.abbv)) {
      if (this.payCheckCityForm.controls['filingStatus'].value) {
        parms.push({ name: 'FILINGSTATUS', value: this.payCheckCityForm.controls['filingStatus'].value }); //GA has special values for this
        let filingStatus = "";
        if (this.selectedState.abbv == "NJ")
          filingStatus = this.calculationConstants.NJFilingStatuses.find(obj => obj.code == this.payCheckCityForm.controls['filingStatus'].value).name;
        else if (this.selectedState.abbv == "NY")
          filingStatus = this.calculationConstants.NYFilingStatuses.find(obj => obj.code == this.payCheckCityForm.controls['filingStatus'].value).name;
        else if (this.selectedState.abbv == "GA")
          filingStatus = this.calculationConstants.GAFilingStatuses.find(obj => obj.code == this.payCheckCityForm.controls['filingStatus'].value).name;
        else if (this.selectedState.abbv == "DC")
          filingStatus = this.calculationConstants.DCFilingStatuses.find(obj => obj.code == this.payCheckCityForm.controls['filingStatus'].value).name;
        else if (this.selectedState.abbv == "UT" || this.selectedState.abbv == "MD")
          filingStatus = this.calculationConstants.UTMDFilingStatuses.find(obj => obj.code == this.payCheckCityForm.controls['filingStatus'].value).name;
        else
          filingStatus = this.calculationConstants.StateFilingStatuses.find(obj => obj.code == this.payCheckCityForm.controls['filingStatus'].value).name;
        this.emailHtmlInputString += divTag + "State Filing Status" + ':</div> ' + filingStatus + '<br/>';
      }
    }
    if (this.calculationConstants.familyLeaveStates.includes(this.selectedState.abbv)) {
      if (this.payCheckCityForm.controls['familyLeave'].value) {
        parms.push({ name: stateCode + '-000-0000-FLI-000', value: this.getBooleanFromYesNo(this.payCheckCityForm.controls['familyLeave'].value) });
        this.emailHtmlInputString += divTag + "FLI" + ':</div> ' + this.payCheckCityForm.controls['familyLeave'].value + '<br/>';
      }
    }
    if (this.calculationConstants.withholdingCodeStates.includes(this.selectedState.abbv)) {
      if (this.payCheckCityForm.controls['withholdingCode'].value != null) {
        parms.push({ name: 'WITHHOLDINGCODE', value: this.payCheckCityForm.controls['withholdingCode'].value });
        this.emailHtmlInputString += divTag + "Withholding Code" + ':</div> ' + this.payCheckCityForm.controls['withholdingCode'].value + '<br/>';
      }
    }
    if (this.calculationConstants.personalExemptionStates.includes(this.selectedState.abbv)) {
      if (this.payCheckCityForm.controls['personalExemptions'].value != null) {
        parms.push({ name: 'PERSONALEXEMPTIONS', value: this.payCheckCityForm.controls['personalExemptions'].value });
        this.emailHtmlInputString += divTag + "Personal Exemptions" + ':</div> ' + this.payCheckCityForm.controls['personalExemptions'].value + '<br/>';
      }
    }
    if (this.calculationConstants.addressStates.includes(this.selectedState.abbv)) {
      local['address1'] = this.payCheckCityForm.controls['address'].value != null ? this.payCheckCityForm.controls['address'].value : '';
      this.emailHtmlInputString += divTag + this.fieldMap.get('address1') + ':</div> ' + '<div style="float: left; width: 620px; word-wrap: break-word; padding:3px;">'+ local['address1'] + '</div>' + '<br/>';
      local['city'] = this.payCheckCityForm.controls['city'].value != null ? this.payCheckCityForm.controls['city'].value : ''; 
      this.emailHtmlInputString += divTag + this.fieldMap.get('city') + ':</div> ' + local['city'] + '<br/>';
      local['state'] = this.payCheckCityForm.controls['state'].value != null ? this.payCheckCityForm.controls['state'].value.abbv : '';
       // don't include state in address section HTML, already at top of input section
      local['zip'] = this.payCheckCityForm.controls['zip'].value != null ? this.payCheckCityForm.controls['zip'].value : '';
      this.emailHtmlInputString += divTag + this.fieldMap.get('zip') + ':</div> ' + local['zip'] + '<br/>'; 
      local['zip4'] = this.payCheckCityForm.controls['zip4'].value != null ? this.payCheckCityForm.controls['zip4'].value : '';
      this.emailHtmlInputString += divTag + this.fieldMap.get('zip4') + ':</div> ' + local['zip4'] + '<br/>';  
      if (this.calculationConstants.residentStates.includes(this.selectedState.abbv)) {
        local['resident'] = this.payCheckCityForm.controls['resident'].value != null ? this.getBooleanFromYesNo(this.payCheckCityForm.controls['resident'].value) : '';
        this.emailHtmlInputString += divTag + this.fieldMap.get('resident') + ':</div> ' + this.getYesNoFromBoolean(local['resident']) + '<br/>';  
      }
    }
    for (let localKey in local) {
      if (local[localKey]) {
        stateInfo['local'] = local;
        break;
      }
    }
    for (let parm of parms) {
      if (parm.value) {
        stateInfo['parms'] = parms;
        break;
      }
    }
    return stateInfo;
  }

  submitForm() {
    if (!this.formType) {
      swal('No Form Type Selected!', `Please select a calculation form type before hitting submit`, 'warning');
      return;
    }
    if (this.clientApiCount == null) {
      swal('Failure!', `Could not retrieve calculation API usage data, cannot request new calculation`, 'error');
      return;
    }
    else if (this.clientApiCount >= this.environment.calculationApiLimitPerClient) {
      swal('Failure!', `Calculation API Limit reached for ${this.client["name"]}: ${this.clientApiCount}/${this.environment.calculationApiLimitPerClient}`, 'error');
      return;
    }
    this.emailHtmlInputString = "";
    this.emailHtmlOutputString = "";
    let payLoad = {};
    let payCheckCityObj = {};
    let generalFields = this.setFixedFields();
    let localFields = this.setLocalFields();
    payCheckCityObj = generalFields;
    if (Object.keys(localFields).length > 0) // Only add the localFields object if the user has entered something
      payCheckCityObj['stateInfo'] = localFields;
    let payCheckCityJSON = JSON.stringify(payCheckCityObj);
    payLoad['jsonText'] = payCheckCityJSON;
    payLoad['calculationType'] = this.formType;
    this.callCalculationEngine(payLoad);
  }

  /* Constructs the output HTML for the email and modal popup, this will navigte through the nested JSON objects and retrieve all the desired data,
     resulting in an HTMLOutputString that is a flattened list of all the key value pairs.
  */
  prepareOutputHtml(calculationData: any) {
    let fieldName;
    let divTag = '<div style="font-weight: 600; width:25%; float:left; padding-left:10px;">'
    this.emailHtmlOutputString += '<div style="background:#115E67; padding:5px; color:#FFFFFF; width: 98%; padding-left: 10px">Calculation Output</div><br/>';
    if (this.formType == "SALARY")
      this.emailHtmlOutputString += divTag + "Gross Pay" + ':</div> ' + this.formatCurrency(calculationData['grossPay']) + '<br/>';
    if (this.formType == "GROSSUP")
      this.emailHtmlOutputString += divTag + "Net Pay" + ':</div> ' + this.formatCurrency(calculationData['netPay']) + '<br/>';
    for (let key in calculationData) {
      if (key != "netPay" && key != "grossPay" && key != 'timeStamp') {
        if (Array.isArray(calculationData[key])) {
          for (let pair of calculationData[key]) {
            if (pair.name) {
              fieldName = this.fieldMap.get(pair.name);
              if (!fieldName)
                fieldName = pair.name;
              this.emailHtmlOutputString += divTag + fieldName + ':</div> ' + this.formatCurrency(pair.value) + '<br/>';
            }
          }
        }
        else {
          if (key == 'state') { // We only want the state name, not the fullName eg: NY: New York -> New York
            fieldName = this.selectedState.fullName.split(":")[1].trim();
          }
          else {
            fieldName = this.fieldMap.get(key);
          }
          if (!fieldName)
            fieldName = key;
          this.emailHtmlOutputString += divTag + fieldName + ':</div> ' + this.formatCurrency(calculationData[key]) + '<br/>';
        }
      }
    }
    if (this.formType == "SALARY")
      this.emailHtmlOutputString += divTag + "Net Pay" + ':</div> ' + this.formatCurrency(calculationData['netPay']) + '<br/>';
    if (this.formType == "GROSSUP")
      this.emailHtmlOutputString += divTag + "Gross Pay" + ':</div> ' + this.formatCurrency(calculationData['grossPay']) + '<br/>';
    return this.emailHtmlOutputString;
  }

  /* Invokes the REST API call to get the current API count for the selected client */
  getCalculationApiCountForClient() {
    this.clientApiCount = null;
    this.showLoader = true;
    this.restCallingService.getCalculationApiCountForClient(this.client['id']).subscribe((resp) => {
      this.showLoader = false;
      this.clientApiCount = Number(resp.json());
      this.apiTextColor = this.getApiTextColor();
    }, err => {
      this.showLoader = false;
      this.clientApiCount = null;
      console.log(err.json());
      swal('Failure!', err.json().message, 'error');
    });
  }

  /* Invokes the REST API call to update the API count for the selected client after a calculation request as been made */
  updateCalculationApiCountForClient() {
    this.showLoader = true;
    this.restCallingService.updateCalculationApiCountForClient(this.client['id']).subscribe((resp) => {
      this.showLoader = false;
      this.getCalculationApiCountForClient();
      console.log(resp.json());
    }, err => {
      this.showLoader = false;
      console.log(err.json());
      swal('Failure!', err.json().message, 'error');
    });
  }

  callCalculationEngine(payLoad: any) {
    this.showLoader = true;
    this.restCallingService.calculationEngineForEmployee(payLoad).subscribe((calculationResponse) => {
      this.showLoader = false;
      console.log(calculationResponse.json());
      this.updateCalculationApiCountForClient();
      let calculationData: any = JSON.parse(calculationResponse.json().jsonText);
      this.emailHtmlOutputString = this.prepareOutputHtml(calculationData.content);
      this.sendEmailAndDisplayResults(this.emailHtmlInputString, this.emailHtmlOutputString);
    }, err => {
      this.showLoader = false;
      console.log(err.json());
      swal('Failure!', err.json().message, 'error');
    });
  }

  sendEmailAndDisplayResults(emailHtmlInputString: string, emailHtmlOutputString: string) {
    let calculationEmailJson = {};
    let emailHtmlTotalString = emailHtmlOutputString + emailHtmlInputString;
    var swalHtml = document.createElement('div');
    swalHtml.style.cssText = 'height: 400px; overflow-y: auto; text-align: left';
    swalHtml.innerHTML = emailHtmlTotalString;
    calculationEmailJson['emailHtml'] = emailHtmlTotalString;
    calculationEmailJson['clientId'] = this.client['id'];
    calculationEmailJson['employeeId'] = this.employee['id'];
    this.sendCalculationEngineEmail(calculationEmailJson);
    swal({ title: 'Calculation Results', content: swalHtml, icon: 'success', buttons: true });
  }

  sendCalculationEngineEmail(calculationEmailJson) {
    this.restCallingService.sendCalculationEmailForEmployee(calculationEmailJson).subscribe((response) => {
    },
      err => {
        console.log(err.json());
        swal('Failure!', err.json().message, 'error');
      });
  }

  formatCurrency(value: any) {
    return Number(value).toLocaleString('en-US', {style: 'currency', currency: 'USD'});
  }

  getApiTextColor() {
    if (this.clientApiCount == null)
      return 'black';
    else if (this.clientApiCount/this.environment.calculationApiLimitPerClient < .5)
      return 'green';
    else if (this.clientApiCount/this.environment.calculationApiLimitPerClient < .7)
      return '#b3b300';
    else if (this.clientApiCount/this.environment.calculationApiLimitPerClient < 1)
      return 'orange';
    else if (this.clientApiCount/this.environment.calculationApiLimitPerClient == 1)
      return 'red';
  }

  getBooleanFromYesNo(str: string) {
    if (str == 'Yes')
      return true;
    else if (str == 'No')
      return false;
    else
      return undefined;
  }

  getYesNoFromBoolean(bool: boolean) {
    if (bool == true)
      return 'Yes';
    else if (bool == false)
      return 'No';
    else
      return undefined;
  }

}


// WEBPACK FOOTER //
// ./src/app/calculation-engine/calculation-engine.component.ts